import { Button } from "@/components/core/buttons/button";
import { Form, FormControl, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form";
import { Input } from "@/components/core/forms/input";
import Select from "@/components/core/forms/select";
import { Textarea } from "@/components/core/forms/textarea";
import { useUpdateContractInvoiceMutation } from "@/features/dashboard/api/queries/contract-invoice-query";
import type { ContractInvoice } from "@/features/dashboard/types/contract-invoice";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useModalControl } from "../core/modals/use-modal-controller";

const editInvoiceSchema = z.object({
	invoice_number: z.string().min(1, "Invoice number is required"),
	amount: z.number().min(0.01, "Amount must be greater than 0"),
	invoice_date: z.string().min(1, "Invoice date is required"),
	due_date: z.string().min(1, "Due date is required"),
	status: z.enum(["pending", "paid", "overdue", "cancelled"]),
	collection_status: z.enum(["pending", "collected"]),
	description: z.string().optional(),
	notes: z.string().optional(),
	payment_method: z.string().optional(),
});

type EditInvoiceFormData = z.infer<typeof editInvoiceSchema>;

interface EditInvoiceModalProps {
	invoice: ContractInvoice;
	onSuccess: () => void;
}

export const EditInvoiceModal: React.FC<EditInvoiceModalProps> = ({ invoice, onSuccess }) => {
	const updateMutation = useUpdateContractInvoiceMutation();

	const { closeModal } = useModalControl();

	const form = useForm<EditInvoiceFormData>({
		resolver: zodResolver(editInvoiceSchema),
		defaultValues: {
			invoice_number: invoice.invoice_number,
			amount: Number.parseFloat(invoice.amount?.toString() || "0"),
			invoice_date: invoice.invoice_date ? format(new Date(invoice.invoice_date), "yyyy-MM-dd") : "",
			due_date: invoice.due_date ? format(new Date(invoice.due_date), "yyyy-MM-dd") : "",
			status: invoice.status as "pending" | "paid" | "overdue" | "cancelled",
			collection_status: invoice.collection_status as "pending" | "collected",
			description: invoice.description || "",
			notes: invoice.notes || "",
			payment_method: invoice.payment_method || "",
		},
	});

	const onSubmit = async (data: EditInvoiceFormData) => {
		try {
			await updateMutation.mutateAsync({
				invoiceId: invoice.id,
				data: {
					...data,
					// Ensure these fields are included even if they're empty strings
					collection_remarks: invoice.collection_remarks,
					date_collected: invoice.date_collected,
					collected_by: invoice.collected_by,
				},
			});
			onSuccess();
		} catch (error) {
			console.error("Error updating invoice:", error);
		}
	};

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col w-full gap-6">
				<div className="flex flex-col w-full gap-6">
					<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
						<h3 className="font-medium text-blue-900 mb-2">Invoice Details</h3>
						<div className="text-sm text-blue-800">
							<p>
								<strong>Invoice ID:</strong> {invoice.id}
							</p>
							<p>
								<strong>Contract:</strong> {invoice.sales_contract?.project_name}
							</p>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="invoice_number"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Invoice Number</FormLabel>
										<FormGroup>
											<Input className="input" {...field} placeholder="INV-001" />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-6">
							<FormField
								control={form.control}
								name="amount"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Amount</FormLabel>
										<FormGroup>
											<Input
												className="input"
												type="number"
												step="0.01"
												placeholder="0.00"
												name={field.name}
												value={field.value || ""}
												onBlur={field.onBlur}
												onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : 0)}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="invoice_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Invoice Date</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-6">
							<FormField
								control={form.control}
								name="due_date"
								render={({ field }) => (
									<FormItem>
										<FormLabel required>Due Date</FormLabel>
										<FormGroup>
											<Input className="input" type="date" {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-6">
							<FormField
								control={form.control}
								name="status"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Status</FormLabel>
										<FormGroup>
											<Select
												placeholder="Select status"
												options={[
													{ value: "pending", label: "Pending" },
													{ value: "paid", label: "Paid" },
													{ value: "overdue", label: "Overdue" },
													{ value: "cancelled", label: "Cancelled" },
												]}
												defaultValue={field.value}
												onValueChange={field.onChange}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="col-span-6">
							<FormField
								control={form.control}
								name="collection_status"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Collection Status</FormLabel>
										<FormGroup>
											<Select
												placeholder="Select collection status"
												options={[
													{ value: "pending", label: "Pending" },
													{ value: "collected", label: "Collected" },
												]}
												defaultValue={field.value}
												onValueChange={field.onChange}
											/>
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Description</FormLabel>
										<FormGroup>
											<Input className="input" {...field} placeholder="Invoice description" />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>

					{form.watch("status") === "paid" && (
						<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
							<div className="col-span-6">
								<FormField
									control={form.control}
									name="payment_method"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Payment Method</FormLabel>
											<FormGroup>
												<Select
													placeholder="Select payment method"
													options={[
														{ value: "cash", label: "Cash" },
														{ value: "bank_transfer", label: "Bank Transfer" },
														{ value: "check", label: "Check" },
														{ value: "credit_card", label: "Credit Card" },
														{ value: "online_payment", label: "Online Payment" },
													]}
													defaultValue={field.value}
													onValueChange={field.onChange}
												/>
											</FormGroup>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					)}

					<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
						<div className="col-span-12">
							<FormField
								control={form.control}
								name="notes"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Notes</FormLabel>
										<FormGroup>
											<Textarea className="input" placeholder="Enter any additional notes..." rows={4} {...field} />
										</FormGroup>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</div>
				</div>

				<div className="flex justify-end gap-2 pt-4 border-t">
					<Button type="button" variant="outline" onClick={onSuccess}>
						Cancel
					</Button>
					<Button type="submit" disabled={updateMutation.isPending} className="bg-blue-600 hover:bg-blue-700">
						{updateMutation.isPending ? "Updating..." : "Update Invoice"}
					</Button>
				</div>
			</form>
		</Form>
	);
};
